import { getApiUrl } from "../config/api";

/**
 * Auth0 Authentication Service
 * Handles login, user validation, and current user retrieval
 */

/**
 * Login or register user with Auth0 credentials
 * @param {Object} auth0User - Auth0 user object
 * @returns {Promise<Object>} User data
 * @throws {Error} If account is disabled or login fails
 */
export const loginWithAuth0 = async (auth0User) => {
  try {
    const response = await fetch(getApiUrl('/api/auth/login/client'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sub: auth0User.sub,
        email: auth0User.email,
        name: auth0User.name,
        picture: auth0User.picture,
        emailVerified: auth0User.email_verified || false
      })
    });

    if (response.status === 403) {
      throw new Error('ACCOUNT_DISABLED');
    }
    
    if (response.status === 404) {
      throw new Error('USER_NOT_FOUND');
    }

    if (!response.ok) {
      throw new Error(`Login failed: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    // Check if it's a network error (fetch failed)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('NETWORK_ERROR');
    }
    // Re-throw other errors
    throw error;
  }
};

/**
 * Get current user information
 * @param {string} auth0Id - Auth0 user ID (sub)
 * @returns {Promise<Object>} User data
 */
export const getCurrentUser = async (auth0Id) => {
  const response = await fetch(getApiUrl(`/api/auth/me?auth0Id=${encodeURIComponent(auth0Id)}`));
  
  if (!response.ok) {
    throw new Error(`Failed to get current user: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Validate if user is an admin
 * @param {string} auth0Id - Auth0 user ID (sub)
 * @returns {Promise<boolean>} True if user is admin
 */
export const validateAdminStatus = async (auth0Id) => {
  const response = await fetch(getApiUrl(`/api/auth/validate-admin?auth0Id=${encodeURIComponent(auth0Id)}`));
  
  if (!response.ok) {
    return false;
  }
  
  return response.json();
};

/**
 * Validate if user account is enabled
 * @param {string} auth0Id - Auth0 user ID (sub)
 * @returns {Promise<boolean>} True if user is enabled
 */
export const validateEnabledStatus = async (auth0Id) => {
  const response = await fetch(getApiUrl(`/api/auth/validate-enabled?auth0Id=${encodeURIComponent(auth0Id)}`));
  
  if (!response.ok) {
    return false;
  }
  
  return response.json();
};
